/**
 * Doses de vacina, ficha do funcionário, comprovante e pendências (#530). Registro e edição são
 * multipart, com o arquivo `proof` opcional; o servidor valida tudo de novo.
 */
import apiClient from './apiClient';
import type {
  FichaDeVacinacao,
  FiltrosDePendencia,
  PaginaDePendencias,
  Vaccination,
  VaccinationPayload,
} from '../types/vaccination';

const FICHA_VAZIA: FichaDeVacinacao = { exigidas: [], naoExigidas: [] };

function paraFormulario(dados: Partial<VaccinationPayload>, comprovante: File | null): FormData {
  const formulario = new FormData();
  for (const [campo, valor] of Object.entries(dados)) {
    if (valor === undefined) continue;
    formulario.append(campo, valor === null ? '' : String(valor));
  }
  if (comprovante) formulario.append('proof', comprovante);
  return formulario;
}

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

class VaccinationService {
  private baseUrl = '/vaccinations';

  async getEmployeeCard(employeeId: string): Promise<FichaDeVacinacao> {
    const response = await apiClient.get<FichaDeVacinacao>(`/employees/${employeeId}/vaccinations`);
    return response.data ?? FICHA_VAZIA;
  }

  async getById(id: string): Promise<Vaccination | undefined> {
    const response = await apiClient.get<Vaccination>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async create(dados: VaccinationPayload, comprovante: File | null): Promise<Vaccination | undefined> {
    const response = await apiClient.post<Vaccination>(this.baseUrl, paraFormulario(dados, comprovante), MULTIPART);
    return response.data;
  }

  /** Funcionário e vacina da dose não mudam na edição: o servidor ignora os dois. */
  async update(
    id: string,
    dados: Partial<Omit<VaccinationPayload, 'employeeId' | 'vaccineTypeId'>>,
    comprovante: File | null
  ): Promise<Vaccination | undefined> {
    const response = await apiClient.put<Vaccination>(
      `${this.baseUrl}/${id}`,
      paraFormulario(dados, comprovante),
      MULTIPART
    );
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async downloadProof(id: string, nomeDoArquivo?: string): Promise<void> {
    await apiClient.downloadFile(`${this.baseUrl}/${id}/proof`, nomeDoArquivo);
  }

  async getPendencies(filtros: FiltrosDePendencia): Promise<PaginaDePendencias> {
    const query = new URLSearchParams();
    if (filtros.companyId) query.set('companyId', filtros.companyId);
    if (filtros.unitId) query.set('unitId', filtros.unitId);
    if (filtros.departmentId) query.set('departmentId', filtros.departmentId);
    if (filtros.vaccineTypeId) query.set('vaccineTypeId', filtros.vaccineTypeId);
    if (filtros.situacao && filtros.situacao.length > 0) query.set('situacao', filtros.situacao.join(','));
    if (filtros.search) query.set('search', filtros.search);
    if (filtros.page) query.set('page', String(filtros.page));
    if (filtros.limit) query.set('limit', String(filtros.limit));

    const response = await apiClient.get<PaginaDePendencias>(`/vaccination-pendencies?${query.toString()}`);
    return response.data ?? { rows: [], total: 0, page: filtros.page ?? 1, limit: filtros.limit ?? 0 };
  }
}

export const vaccinationService = new VaccinationService();
export default vaccinationService;
